import { Component, OnInit, OnDestroy, Output, EventEmitter, ChangeDetectorRef, ChangeDetectionStrategy, inject, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subscription } from 'rxjs';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  buildHeaderDataShell,
  generalConfigFromGlobal,
  normalizeFilterParams,
  rowPayloadFromActiveScreen,
} from '../../../shared/workspace-element-context';
import { ExpenseTransAccountDetailsService, AccountInfo, ScheduleMInfo } from './expense-trans-account-details.service';

@Component({
    selector: 'gtw-expense-trans-account-details',
    templateUrl: './expense-trans-account-details.component.html',
    styleUrls: ['./expense-trans-account-details.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class ExpenseTransAccountDetailsComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);


  // ============================================
  // OUTPUTS
  // ============================================
  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  private readonly subs = new Subscription();

  baseURLs: any = { 'admin': '', 'api': '', 'custom': '', 'efile': '', 'pdfEngine': '', 'bulkPdfEngine': '' };

  filterParams: any = {};

  globalService: any;
  appService: any = {
    USER_SETTINGS: '',
    SERVER_CONFIG: ''
  };
  GENERAL_CONFIG: any;

  rowData: any = {};

  // Gross Income info (needed to determine if Local Account column should be shown)
  grossIncome: any = {};

  // ============================================
  // COMPONENT STATE
  // ============================================

  isLoading = true;
  accountInformation: AccountInfo[] = [];
  scheduleMInformation: ScheduleMInfo[] = [];

  // Display mode based on MAP_DESC
  get isAccountMode(): boolean {
    return this.rowData?.MAP_DESC === 'by Account';
  }

  get isScheduleMMode(): boolean {
    return this.rowData?.MAP_DESC === 'by Schedule M';
  }

  // Get DISP_ACCT_CODE from grossIncome or fallback to rowData (matching AngularJS behavior)
  get dispAcctCode(): string {
    return this.grossIncome?.DISP_ACCT_CODE || this.rowData?.DISP_ACCT_CODE || '';
  }

  // Check if we have valid data to display (table should be shown)
  get hasValidData(): boolean {
    return !!this.dispAcctCode;
  }

  get showLocalAccountColumn(): boolean {
    // Local Account column is hidden when DISP_ACCT_CODE is 'Asset' or 'Asset Exp'
    const dispCode = this.dispAcctCode;
    return dispCode !== 'Asset' && dispCode !== 'Asset Exp';
  }

  private initComplete = false;

  // ============================================
  // CONSTRUCTOR
  // ============================================

  constructor(
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService,
    private service: ExpenseTransAccountDetailsService,
    private cdr: ChangeDetectorRef
  ) {}

  // ============================================
  // LIFECYCLE
  // ============================================

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.subs.add(this.gtwWorkspace.setTabActive.subscribe(() => {
      this.applyWorkspaceContext();
      this.init();
    }));
    this.subs.add(this.gtwGlobal.globalParamsChanged$.subscribe(() => {
      this.applyWorkspaceContext();
      this.init();
    }));
    this.init();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    this.initComplete = false;
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const header = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    this.baseURLs = generalConfigFromGlobal(this.gtwGlobal);
    this.GENERAL_CONFIG = header.generalConfigData || {};
    this.globalService = header;
    this.appService.USER_SETTINGS = header.userSettingsData || {};
    this.appService.SERVER_CONFIG = header.serverConfigData || {};
    if (header.globalParams) {
      this.service.setGlobalParams(header.globalParams);
    }
    this.filterParams = normalizeFilterParams(activeScreen);
    this.service.setFilterParams(this.filterParams);
    const row = rowPayloadFromActiveScreen(activeScreen);
    this.rowData = Array.isArray(row) ? (row[0] || {}) : row;
    this.grossIncome =
      activeScreen?.data?.grossIncome || activeScreen?.data?.gross_income || {};
  }

  // ============================================
  // INITIALIZATION
  // ============================================

  private init(): void {
    // Check if we have all required data
    console.log('[AccountDetails] init() called', {
      hasBaseUrl: !!this.GENERAL_CONFIG?.base_url,
      hasTransCatgKey: !!this.rowData?.TRANS_CATG_KEY,
      rowData: this.rowData,
      grossIncome: this.grossIncome,
      dispAcctCode: this.dispAcctCode,
      isAccountMode: this.isAccountMode,
      isScheduleMMode: this.isScheduleMMode
    });

    if (!this.GENERAL_CONFIG?.base_url || !this.rowData?.TRANS_CATG_KEY) {
      console.log('[AccountDetails] Missing required data, skipping init');
      return;
    }

    if (this.initComplete) {
      console.log('[AccountDetails] Already initialized, skipping');
      return;
    }

    this.initComplete = true;
    this.loadData();
  }

  private loadData(): void {
    const baseUrl = this.GENERAL_CONFIG.base_url;
    const transCatgKey = this.rowData.TRANS_CATG_KEY;

    console.log('[AccountDetails] loadData()', {
      baseUrl,
      transCatgKey,
      isAccountMode: this.isAccountMode,
      isScheduleMMode: this.isScheduleMMode,
      mapDesc: this.rowData?.MAP_DESC
    });

    this.isLoading = true;

    if (this.isAccountMode) {
      console.log('[AccountDetails] Loading account info...');
      this.service.getAccountInfo(baseUrl, transCatgKey).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
        (data: AccountInfo[]) => {
          console.log('[AccountDetails] Account info loaded:', data);
          this.accountInformation = data;
          this.isLoading = false;
          this.cdr.detectChanges();
        },
        error => {
          console.error('[AccountDetails] Error loading account info:', error);
          this.isLoading = false;
          this.cdr.detectChanges();
        }
      );
    } else if (this.isScheduleMMode) {
      console.log('[AccountDetails] Loading schedule M info...');
      this.service.getScheduleMInfo(baseUrl, transCatgKey).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
        (data: ScheduleMInfo[]) => {
          console.log('[AccountDetails] Schedule M info loaded:', data);
          this.scheduleMInformation = data;
          this.isLoading = false;
          this.cdr.detectChanges();
        },
        error => {
          console.error('[AccountDetails] Error loading schedule M info:', error);
          this.isLoading = false;
          this.cdr.detectChanges();
        }
      );
    } else {
      console.log('[AccountDetails] No matching mode (not by Account or by Schedule M), MAP_DESC:', this.rowData?.MAP_DESC);
      this.isLoading = false;
      this.cdr.detectChanges();
    }
  }

  // ============================================
  // ACTIONS
  // ============================================

  cancel(): void {
    this.requestGridUpdate.emit('cancel');
  }

  // ============================================
  // HELPERS
  // ============================================

  /**
   * Format System Account description
   * If value is '-' or empty, return empty string
   */
  formatSysAcctDesc(value: string | undefined): string {
    if (!value) return '';
    const trimmed = value.trim();
    return trimmed === '-' ? '' : trimmed;
  }
}
